import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useApiIsLoaded, useMapsLibrary } from '@vis.gl/react-google-maps'
import * as Dialog from '@radix-ui/react-dialog'
import {
  X,
  ChevronLeft,
  ChevronRight,
  Download,
  Camera,
  Calendar,
  Clock,
  MapPin,
  ImageOff,
  Loader2,
} from 'lucide-react'

import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { formatDateTime } from '@/lib/utils'
import { getEventDownloads, downloadFile } from '@/lib/eventDownload'
import type { Event, VisualDetection } from '@/types'
// Detection icon imports moved to shared DetectionChip component

// DetectionChip — imported from shared UI component (aliased as ModalChip for local usage)
import { DetectionChip as ModalChip } from '@/components/ui/detection-chip'

// ─── Media panel with pinch/drag zoom ────────────────────────────────────────

const MIN_SCALE = 1
const MAX_SCALE = 5

// Matches the Radix Dialog open animation (data-[state=open]:duration-350) with a small
// buffer so the <video> element mounts only after the modal is fully settled. This is the
// foundation of the two-phase video reveal — see ModalMedia.
const MODAL_OPEN_ANIMATION_MS = 380

function clamp(val: number, min: number, max: number) {
  return Math.min(Math.max(val, min), max)
}

function ModalMedia({ event }: { event: Event }) {
  const { t } = useTranslation('events')

  const isVideo = event.media_details?.media_type === 'video'
  const videoUrl = event.detection_video_url
  const thumbnailUrl = event.thumbnail_url

  const [imageStatus, setImageStatus] = useState<'loading' | 'loaded' | 'error'>(
    thumbnailUrl ? 'loading' : 'error'
  )
  // #078: two-phase video reveal.
  // Phase 1 (modal-open animation): only the thumbnail <img> is rendered.
  // Phase 2 (after MODAL_OPEN_ANIMATION_MS): the <video> mounts at opacity 0 with autoplay;
  // when it fires `playing`, fade it in over the thumbnail. This eliminates the race between
  // the Radix open animation and the browser's media-pipeline init (metadata load, decoder
  // allocation, controls paint, first-frame paint) that produced the "nudge" we saw.
  const [modalSettled, setModalSettled] = useState(false)
  const [videoPlaying, setVideoPlaying] = useState(false)

  // ── Zoom / pan state ──
  const [scale, setScale] = useState(1)
  const [offset, setOffset] = useState({ x: 0, y: 0 })
  const containerRef = useRef<HTMLDivElement>(null)

  // Derived
  const isZoomed = scale > 1.01

  // Reset all transient state when navigating to a different event.
  useEffect(() => {
    setScale(1)
    setOffset({ x: 0, y: 0 })
    setImageStatus(thumbnailUrl ? 'loading' : 'error')
    setModalSettled(false)
    setVideoPlaying(false)
  }, [event.id, thumbnailUrl])

  // Phase 1 → Phase 2 transition: wait until the modal's open animation has fully run
  // before mounting the <video> element. During this window only the thumbnail is on screen
  // (it's an <img>, so its size lands with the first paint — no layout shift).
  useEffect(() => {
    if (!isVideo) return
    const id = window.setTimeout(() => setModalSettled(true), MODAL_OPEN_ANIMATION_MS)
    return () => window.clearTimeout(id)
  }, [isVideo, event.id])

  // ── Pan helpers ──
  // Clamp offset so media never fully leaves the viewport
  const clampOffset = useCallback(
    (x: number, y: number, s: number): { x: number; y: number } => {
      if (!containerRef.current) return { x, y }
      const { width, height } = containerRef.current.getBoundingClientRect()
      // Max pan = half the "extra" size created by scale
      const maxX = (width * (s - 1)) / 2
      const maxY = (height * (s - 1)) / 2
      return { x: clamp(x, -maxX, maxX), y: clamp(y, -maxY, maxY) }
    },
    []
  )

  // ── Desktop: mouse wheel zoom ──
  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey && !e.metaKey) return // plain scroll = navigate detail panel, not zoom
      e.preventDefault()
      const delta = e.deltaY > 0 ? -0.15 : 0.15
      setScale((prev) => {
        const next = clamp(prev + delta, MIN_SCALE, MAX_SCALE)
        if (next === MIN_SCALE) setOffset({ x: 0, y: 0 })
        return next
      })
    }
    el.addEventListener('wheel', onWheel, { passive: false })
    return () => el.removeEventListener('wheel', onWheel)
  }, [])

  // ── Desktop: double-click to zoom in/out ──
  const handleDoubleClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      // Don't zoom when clicking native video controls (bottom ~44px)
      if (isVideo) {
        const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect()
        if (e.clientY > rect.bottom - 48) return
      }
      if (isZoomed) {
        setScale(1)
        setOffset({ x: 0, y: 0 })
      } else {
        setScale(2)
        setOffset({ x: 0, y: 0 })
      }
    },
    [isZoomed, isVideo]
  )

  // ── Desktop: click-drag to pan ──
  const dragStart = useRef<{ x: number; y: number; ox: number; oy: number } | null>(null)

  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!isZoomed) return
      e.preventDefault()
      dragStart.current = { x: e.clientX, y: e.clientY, ox: offset.x, oy: offset.y }
      setIsDragging(true)
    },
    [isZoomed, offset]
  )

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (!dragStart.current) return
      const dx = e.clientX - dragStart.current.x
      const dy = e.clientY - dragStart.current.y
      setOffset(
        clampOffset(dragStart.current.ox + dx, dragStart.current.oy + dy, scale)
      )
    }
    const onMouseUp = () => {
      dragStart.current = null
      setIsDragging(false)
    }
    window.addEventListener('mousemove', onMouseMove)
    window.addEventListener('mouseup', onMouseUp)
    return () => {
      window.removeEventListener('mousemove', onMouseMove)
      window.removeEventListener('mouseup', onMouseUp)
    }
  }, [scale, clampOffset])

  // ── Mobile: pinch-to-zoom + single-finger pan ──
  const lastTouches = useRef<{ dist: number; mx: number; my: number; ox: number; oy: number } | null>(null)

  const getTouchDist = (t: React.TouchList) =>
    Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY)

  const handleTouchStart = useCallback(
    (e: React.TouchEvent<HTMLDivElement>) => {
      if (e.touches.length === 2) {
        lastTouches.current = {
          dist: getTouchDist(e.touches),
          mx: (e.touches[0].clientX + e.touches[1].clientX) / 2,
          my: (e.touches[0].clientY + e.touches[1].clientY) / 2,
          ox: offset.x,
          oy: offset.y,
        }
      } else if (e.touches.length === 1 && isZoomed) {
        dragStart.current = {
          x: e.touches[0].clientX,
          y: e.touches[0].clientY,
          ox: offset.x,
          oy: offset.y,
        }
        setIsDragging(true)
      }
    },
    [offset, isZoomed]
  )

  const handleTouchMove = useCallback(
    (e: React.TouchEvent<HTMLDivElement>) => {
      if (e.touches.length === 2 && lastTouches.current) {
        e.preventDefault()
        const newDist = getTouchDist(e.touches)
        const ratio = newDist / lastTouches.current.dist
        setScale((prev) => {
          const next = clamp(prev * ratio, MIN_SCALE, MAX_SCALE)
          if (next === MIN_SCALE) setOffset({ x: 0, y: 0 })
          return next
        })
        lastTouches.current.dist = newDist
      } else if (e.touches.length === 1 && dragStart.current) {
        e.preventDefault()
        const dx = e.touches[0].clientX - dragStart.current.x
        const dy = e.touches[0].clientY - dragStart.current.y
        setOffset(
          clampOffset(dragStart.current.ox + dx, dragStart.current.oy + dy, scale)
        )
      }
    },
    [scale, clampOffset]
  )

  const handleTouchEnd = useCallback(() => {
    lastTouches.current = null
    dragStart.current = null
    setIsDragging(false)
  }, [])

  // ── Cursor logic — use state so React re-renders on drag start/end ──
  const [isDragging, setIsDragging] = useState(false)
  const cursor = isZoomed ? (isDragging ? 'cursor-grabbing' : 'cursor-grab') : 'cursor-default'

  const transformStyle = {
    transform: `scale(${scale}) translate(${offset.x / scale}px, ${offset.y / scale}px)`,
    transformOrigin: 'center center',
    transition: dragStart.current ? 'none' : 'transform 0.15s ease-out',
  }

  return (
    <div
      ref={containerRef}
      className={cn(
        'relative w-full h-full bg-black flex items-center justify-center min-h-0 overflow-hidden select-none',
        cursor,
        // Prevent browser pan/zoom when we handle it ourselves
        isZoomed ? '[touch-action:none]' : '[touch-action:auto]'
      )}
      onDoubleClick={handleDoubleClick}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Media */}
      <div style={transformStyle} className="w-full h-full flex items-center justify-center">
        {isVideo && videoUrl ? (
          // Two-phase reveal — see comment in ModalMedia.
          // Phase 1: thumbnail only. Phase 2 (after modal settles): <video> mounts and fades in.
          <div className="relative w-full h-full flex items-center justify-center">
            {/* Thumbnail is always rendered behind the video so the swap is a paint-only fade.
                Pinned to w-full h-full with object-contain to match the video's letterbox box. */}
            {thumbnailUrl && (
              <img
                src={thumbnailUrl}
                alt=""
                aria-hidden
                draggable={false}
                className="absolute inset-0 w-full h-full object-contain pointer-events-none select-none"
              />
            )}
            {modalSettled && (
              <video
                key={videoUrl}
                src={videoUrl}
                controls
                autoPlay
                muted
                loop
                playsInline
                preload="auto"
                onPlaying={() => setVideoPlaying(true)}
                className={cn(
                  'relative w-full h-full object-contain transition-opacity duration-200',
                  videoPlaying ? 'opacity-100' : 'opacity-0'
                )}
                // Prevent drag on the video element itself from conflicting
                onMouseDown={(e) => isZoomed && e.preventDefault()}
              />
            )}
          </div>
        ) : thumbnailUrl && imageStatus !== 'error' ? (
          <>
            {imageStatus === 'loading' && (
              <div className="absolute inset-0 flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-white/40" />
              </div>
            )}
            <img
              key={thumbnailUrl}
              src={thumbnailUrl}
              alt={event.event_type}
              draggable={false}
              className={cn(
                'max-h-full max-w-full object-contain transition-opacity duration-300',
                imageStatus === 'loaded' ? 'opacity-100' : 'opacity-0'
              )}
              onLoad={() => setImageStatus('loaded')}
              onError={() => setImageStatus('error')}
            />
          </>
        ) : (
          <div className="flex flex-col items-center gap-2 text-white/30">
            <ImageOff className="h-12 w-12" />
            <span className="text-sm">{t('detail.noMedia')}</span>
          </div>
        )}
      </div>

      {/* Zoom level indicator — shown when zoomed */}
      {isZoomed && (
        <div className="absolute top-3 left-3 rounded-full bg-black/60 backdrop-blur-sm text-white text-xs px-2.5 py-1 font-medium pointer-events-none select-none">
          {scale.toFixed(1)}×
        </div>
      )}

      {/* Zoom hint — shown at 1× on desktop to hint double-click */}
      {!isZoomed && (
        <div className="absolute bottom-12 left-1/2 -translate-x-1/2 rounded-full bg-black/40 backdrop-blur-sm text-white/50 text-[10px] px-2.5 py-1 pointer-events-none select-none hidden lg:block">
          {t('detail.zoomHint')}
        </div>
      )}
    </div>
  )
}

// ─── Street View panel ────────────────────────────────────────────────────────

function StreetViewCanvas({
  lat,
  lng,
  onStatus,
}: {
  lat: number
  lng: number
  onStatus: (s: 'loading' | 'ok' | 'no_results') => void
}) {
  const apiReady = useApiIsLoaded()
  const streetViewLib = useMapsLibrary('streetView')
  const containerRef = useRef<HTMLDivElement | null>(null)
  const panoramaRef = useRef<google.maps.StreetViewPanorama | null>(null)

  useEffect(() => {
    if (!apiReady || !streetViewLib || !containerRef.current) return
    let cancelled = false
    const svService = new google.maps.StreetViewService()
    svService.getPanorama({ location: { lat, lng }, radius: 50 }, (data, status) => {
      if (cancelled) return
      if (status === google.maps.StreetViewStatus.OK && data?.location?.pano) {
        // Dispose previous panorama before creating a new one
        if (panoramaRef.current) {
          panoramaRef.current.setVisible(false)
          panoramaRef.current = null
        }
        panoramaRef.current = new google.maps.StreetViewPanorama(containerRef.current!, {
          pano: data.location.pano,
          pov: { heading: 0, pitch: 0 },
          motionTracking: false,
          motionTrackingControl: false,
        })
        onStatus('ok')
      } else {
        onStatus('no_results')
      }
    })
    return () => {
      cancelled = true
      if (panoramaRef.current) {
        panoramaRef.current.setVisible(false)
        panoramaRef.current = null
      }
    }
  }, [apiReady, streetViewLib, lat, lng, onStatus])

  return <div ref={containerRef} style={{ width: '100%', height: 360 }} />
}

function StreetViewPanel({ lat, lng }: { lat: number; lng: number }) {
  const { t } = useTranslation('events')
  const [expanded, setExpanded] = useState(false)
  const [panoStatus, setPanoStatus] = useState<'loading' | 'ok' | 'no_results'>('loading')

  if (!expanded) {
    return (
      <button
        type="button"
        onClick={() => setExpanded(true)}
        className="text-sm text-primary hover:underline inline-flex items-center gap-1"
      >
        {t('streetView.title')}
      </button>
    )
  }

  return (
    <div className="mt-2 border rounded-md overflow-hidden bg-muted">
      <div className="flex items-center justify-between px-3 py-2 text-xs">
        <span className="font-medium">{t('streetView.title')}</span>
        <button
          type="button"
          onClick={() => setExpanded(false)}
          className="text-muted-foreground hover:text-foreground"
          aria-label="Close Street View"
        >
          ×
        </button>
      </div>
      <StreetViewCanvas lat={lat} lng={lng} onStatus={setPanoStatus} />
      {panoStatus === 'no_results' && (
        <div className="px-3 py-3 text-sm text-muted-foreground">
          {t('streetView.notAvailable')}{' '}
          <a
            href={`https://www.google.com/maps?q=${lat},${lng}`}
            target="_blank"
            rel="noreferrer"
            className="underline text-primary"
          >
            {t('streetView.openInGoogleMaps')}
          </a>
        </div>
      )}
    </div>
  )
}

// ─── Detail panel ─────────────────────────────────────────────────────────────

function DetailPanel({ event, onClose }: { event: Event; onClose: () => void }) {
  const { t } = useTranslation('events')
  const { t: tc } = useTranslation('common')

  // Note: markAsRead intentionally disabled — backend EventViewSet has no /read/ action yet.
  // Re-enable once the backend adds: @action(detail=True, methods=['post'], url_path='read')

  const eventTypeDisplay = tc(`eventTypes.${event.event_type}`, { defaultValue: event.event_type.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()) })

  const confidenceColor =
    event.confidence_score >= 0.8
      ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
      : event.confidence_score >= 0.5
      ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400'
      : 'bg-rose-500/10 text-rose-600 dark:text-rose-400'

  // Per-object confidence map from metadata — max confidence per class across all frames
  // (the best detection of each class), not the arbitrary last-frame value. (#095 follow-up)
  const detectionMap: Record<string, number> = {}
  const metaDetections = (event.metadata?.detections as VisualDetection[]) ?? []
  metaDetections.forEach((d) => { detectionMap[d.class_name] = Math.max(detectionMap[d.class_name] ?? 0, d.confidence) })

  const objectClasses = event.detected_objects ?? []
  const audioDetections = event.audio_detections ?? []

  // #094 — use the EVENT's frozen location snapshot (where the photo was taken),
  // NOT the camera's current position. Pre-#094 events have no snapshot (null) and
  // are shown as "location not recorded" rather than pinned to the camera's latest spot.
  const lat = event.latitude != null ? Number(event.latitude) : null
  const lng = event.longitude != null ? Number(event.longitude) : null
  const hasCoords =
    lat != null && lng != null && !Number.isNaN(lat) && !Number.isNaN(lng) && (lat !== 0 || lng !== 0)
  const locationAddress = event.location_address

  const { original, annotated } = getEventDownloads(event)

  return (
    <div className="flex flex-col h-full overflow-y-auto">
      {/* Header */}
      <div className="p-4 border-b border-border/60 flex-shrink-0">
        {/* Top row: title + confidence + close */}
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <h2 className="text-base font-semibold leading-tight">{eventTypeDisplay}</h2>
            <div className="flex items-center gap-1.5 mt-1 text-sm text-muted-foreground">
              <Camera className="h-3.5 w-3.5 flex-shrink-0" />
              <span className="truncate">{event.camera_name}</span>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className={cn('inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold', confidenceColor)}>
              {Math.round(event.confidence_score * 100)}%
            </span>
            <Dialog.Close asChild>
              <button
                onClick={onClose}
                className="flex h-7 w-7 items-center justify-center rounded-full text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                aria-label={t('detail.close')}
              >
                <X className="h-4 w-4" />
              </button>
            </Dialog.Close>
          </div>
        </div>

        {/* Timestamps */}
        <div className="mt-3 space-y-1">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Calendar className="h-3.5 w-3.5 flex-shrink-0" />
            <span>{formatDateTime(event.occurred_at)}</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Clock className="h-3.5 w-3.5 flex-shrink-0" />
            <span>{t('detail.created', { date: formatDateTime(event.created_at) })}</span>
          </div>
          {/* #094 — capture-time location snapshot (address → coords → not recorded) */}
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
            {hasCoords ? (
              <span className="truncate">
                {locationAddress ?? `${lat!.toFixed(5)}, ${lng!.toFixed(5)}`}
              </span>
            ) : (
              <span className="italic">{t('detail.locationNotRecorded')}</span>
            )}
          </div>
        </div>
      </div>

      {/* Detection chips */}
      {(objectClasses.length > 0 || audioDetections.length > 0) && (
        <div className="p-5 pb-4 border-b border-border/60 space-y-3 flex-shrink-0">
          {objectClasses.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground/60 mb-2">
                {t('detail.detectedObjects')}
              </p>
              <div className="flex flex-wrap gap-1.5">
                {objectClasses.map((cls) => (
                  <ModalChip key={cls} cls={cls} confidence={detectionMap[cls]} type="object" />
                ))}
              </div>
            </div>
          )}
          {audioDetections.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground/60 mb-2">
                {t('detail.audioEvents')}
              </p>
              <div className="flex flex-wrap gap-1.5">
                {audioDetections.map((d) => (
                  <ModalChip key={d.class_name} cls={d.class_name} confidence={d.confidence} type="audio" />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Description */}
      {event.description && (
        <div className="p-5 pb-4 border-b border-border/60 flex-shrink-0">
          <p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground/60 mb-2">
            {t('detail.description')}
          </p>
          <p className="text-sm text-muted-foreground leading-relaxed">{event.description}</p>
        </div>
      )}

      {/* Metadata grid */}
      <div className="p-5 pb-4 border-b border-border/60 flex-shrink-0">
        <p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground/60 mb-3">
          {t('detail.eventInfo')}
        </p>
        <dl className="grid grid-cols-2 gap-x-4 gap-y-2.5">
          <div>
            <dt className="text-[10px] text-muted-foreground/60 uppercase tracking-wide">{t('detail.eventId')}</dt>
            <dd className="text-xs font-medium mt-0.5">#{event.id}</dd>
          </div>
          <div>
            <dt className="text-[10px] text-muted-foreground/60 uppercase tracking-wide">{t('detail.mediaType')}</dt>
            <dd className="text-xs font-medium mt-0.5 capitalize">
              {event.media_details?.media_type ?? t('types.image')}
            </dd>
          </div>
          {event.media_details?.resolution && (
            <div>
              <dt className="text-[10px] text-muted-foreground/60 uppercase tracking-wide">{t('detail.resolution')}</dt>
              <dd className="text-xs font-medium mt-0.5">{event.media_details.resolution}</dd>
            </div>
          )}
          {event.media_details?.duration != null && (
            <div>
              <dt className="text-[10px] text-muted-foreground/60 uppercase tracking-wide">{t('detail.duration')}</dt>
              <dd className="text-xs font-medium mt-0.5">{event.media_details.duration}s</dd>
            </div>
          )}
        </dl>
      </div>

      {/* Street View */}
      {hasCoords && (
        <div className="px-5 pb-4 border-b border-border/60 flex-shrink-0">
          <StreetViewPanel lat={lat!} lng={lng!} />
        </div>
      )}

      {/* Actions */}
      <div className="p-5 mt-auto flex-shrink-0 space-y-2">
        {annotated && (
          <Button
            variant="outline"
            className="w-full gap-2"
            onClick={() => downloadFile(annotated.url, annotated.filename)}
          >
            <Download className="h-4 w-4" />
            {t('detail.downloadAnnotated')}
          </Button>
        )}
        {original && (
          <Button
            variant="ghost"
            className="w-full gap-2 text-muted-foreground"
            onClick={() => downloadFile(original.url, original.filename)}
          >
            <Download className="h-4 w-4" />
            {t('detail.downloadOriginal')}
          </Button>
        )}
      </div>
    </div>
  )
}

// ─── Main modal ───────────────────────────────────────────────────────────────

interface EventDetailModalProps {
  events: Event[]
  selectedIndex: number | null
  onClose: () => void
  onNavigate: (index: number) => void
}

export function EventDetailModal({
  events,
  selectedIndex,
  onClose,
  onNavigate,
}: EventDetailModalProps) {
  const { t } = useTranslation('events')

  const isOpen = selectedIndex !== null
  const event = selectedIndex !== null ? events[selectedIndex] : null
  const hasPrev = selectedIndex !== null && selectedIndex > 0
  const hasNext = selectedIndex !== null && selectedIndex < events.length - 1

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft' && hasPrev) onNavigate(selectedIndex! - 1)
      if (e.key === 'ArrowRight' && hasNext) onNavigate(selectedIndex! + 1)
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [isOpen, hasPrev, hasNext, selectedIndex, onNavigate])

  if (!event) return null

  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => { if (!open) onClose() }}>
      <Dialog.Portal>
        {/* Backdrop — slightly slower than modal so it lingers a touch */}
        <Dialog.Overlay
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:duration-250 data-[state=open]:duration-300"
          style={{ animationTimingFunction: 'cubic-bezier(0.32, 0.72, 0, 1)' }}
        />

        {/* Modal container */}
        <Dialog.Content
          className={cn(
            'fixed z-50 inset-0 m-auto',
            // Size: near fullscreen on mobile, constrained on desktop
            'w-[calc(100vw-2rem)] max-w-6xl h-[calc(100vh-2rem)] max-h-[92vh]',
            'bg-background border border-border rounded-xl shadow-2xl overflow-hidden',
            'flex flex-col',
            // Animations — open: scale up + fade + subtle rise; close: reverse
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'data-[state=open]:zoom-in-90 data-[state=closed]:zoom-out-95',
            'data-[state=open]:slide-in-from-bottom-3 data-[state=closed]:slide-out-to-bottom-1',
            'data-[state=open]:duration-350 data-[state=closed]:duration-200',
          )}
          style={{ animationTimingFunction: 'cubic-bezier(0.32, 0.72, 0, 1)' }}
          aria-describedby={undefined}
        >
          <Dialog.Title className="sr-only">
            {t('detail.title', { type: event.event_type })}
          </Dialog.Title>

          {/* Content — two-column on lg, stacked on mobile */}
          <div className="flex flex-col lg:flex-row flex-1 min-h-0 overflow-hidden">
            {/* Left: Media panel */}
            <div className="relative flex-1 min-h-0 bg-black lg:rounded-tl-xl lg:rounded-bl-xl overflow-hidden">
              <ModalMedia key={event.id} event={event} />

              {/* Nav arrows overlaid on media */}
              {hasPrev && (
                <button
                  onClick={() => onNavigate(selectedIndex! - 1)}
                  className="absolute left-3 top-1/2 -translate-y-1/2 flex h-10 w-10 items-center justify-center rounded-full bg-black/50 backdrop-blur-sm text-white hover:bg-black/70 transition-colors"
                  aria-label={t('navigation.previous')}
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
              )}
              {hasNext && (
                <button
                  onClick={() => onNavigate(selectedIndex! + 1)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 flex h-10 w-10 items-center justify-center rounded-full bg-black/50 backdrop-blur-sm text-white hover:bg-black/70 transition-colors"
                  aria-label={t('navigation.next')}
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              )}

              {/* Position counter + keyboard hint */}
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1">
                <div className="rounded-full bg-black/50 backdrop-blur-sm text-white text-xs px-3 py-1 font-medium">
                  {selectedIndex! + 1} / {events.length}
                </div>
                {events.length > 1 && (
                  <span className="text-[10px] text-white/50 hidden sm:block">
                    ← → {t('navigation.keyboardHint', { defaultValue: 'to navigate' })}
                  </span>
                )}
              </div>
            </div>

            {/* Right: Detail panel */}
            <div className="w-full lg:w-80 xl:w-96 flex-shrink-0 border-t lg:border-t-0 lg:border-l border-border/60 overflow-hidden">
              <DetailPanel key={event.id} event={event} onClose={onClose} />
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
